/**
 * Blood Health AI — cart + purchase flow.
 * Enhances elements marked with data-bh-* attributes; the buy mode is decided
 * server-side in Liquid, so this file never branches on settings. With JS
 * disabled every hook falls back to a plain link or a native form POST.
 */
(() => {
  const drawer = document.getElementById('bh-cart-drawer'); // null in checkout mode
  const i18nEl = document.querySelector('[data-bh-cart-i18n]');
  let i18n = {};
  try {
    i18n = i18nEl ? JSON.parse(i18nEl.textContent) : {};
  } catch (e) {
    i18n = {};
  }

  const currency =
    (drawer && drawer.dataset.currency) ||
    document.querySelector('[data-bh-variants]')?.dataset.currency ||
    'USD';
  const money = (cents) =>
    new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency,
    }).format(cents / 100);

  // --- Cart API ---
  const jsonOrThrow = async (res) => {
    const body = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(body.description || body.message || i18n.addError || 'Cart error');
    }
    return body;
  };
  const getCart = () => fetch('/cart.js').then(jsonOrThrow);
  const addItem = (payload) =>
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    }).then(jsonOrThrow);
  const addForm = (form) =>
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: new FormData(form),
    }).then(jsonOrThrow);
  const changeLine = (key, quantity) =>
    fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    }).then(jsonOrThrow);

  // --- Badges (header count bubble) ---
  const updateBadges = (count) => {
    document.querySelectorAll('[data-bh-cart-count]').forEach((el) => {
      el.textContent = count;
      el.hidden = count === 0;
    });
    if (drawer) {
      const inline = drawer.querySelector('[data-bh-cart-count-inline]');
      if (inline) inline.textContent = count > 0 ? `(${count})` : '';
    }
  };

  // --- Drawer ---
  let busy = false;
  let lastTrigger = null;
  let fetched = false;
  // Monotonic token: a render only applies if no newer fetch superseded it,
  // so a stale /cart.js response can never overwrite a post-add render.
  let cartSeq = 0;
  let inflight = null;

  const setBusy = (state) => {
    busy = state;
    if (drawer) drawer.setAttribute('aria-busy', state ? 'true' : 'false');
  };

  const setTriggerBusy = (trigger, state) => {
    if (!trigger) return;
    trigger.toggleAttribute('disabled', state);
    trigger.setAttribute('aria-disabled', state ? 'true' : 'false');
    trigger.setAttribute('aria-busy', state ? 'true' : 'false');
  };

  // Loading row shown only before the first successful render — afterwards the
  // existing aria-busy dimming covers in-place refreshes.
  const setLoading = (state) => {
    if (!drawer) return;
    const el = drawer.querySelector('[data-bh-cart-loading]');
    if (el) el.hidden = !state;
  };

  // Fetch + render behind the seq guard. Plain refreshes share one in-flight
  // request; `force` (post-mutation) always issues a fresh one.
  const refreshCart = (force) => {
    if (force || !inflight) {
      const seq = ++cartSeq;
      const p = getCart()
        .then((cart) => {
          fetched = true;
          if (seq === cartSeq) render(cart);
          return cart;
        })
        .finally(() => {
          if (inflight === p) inflight = null;
        });
      inflight = p;
    }
    return inflight;
  };

  const showError = (msg) => {
    if (!drawer) return;
    const region = drawer.querySelector('[data-bh-cart-error]');
    region.textContent = msg;
    region.hidden = false;
  };

  const clearError = () => {
    if (!drawer) return;
    const region = drawer.querySelector('[data-bh-cart-error]');
    region.hidden = true;
  };

  const lineItem = (item) => {
    const li = document.createElement('li');
    li.className = 'bh-cart-item';
    li.dataset.lineKey = item.key;

    const media = document.createElement('div');
    media.className = 'bh-cart-item__media';
    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image + (item.image.includes('?') ? '&' : '?') + 'width=160';
      img.alt = '';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.width = 160;
      img.height = 160;
      media.appendChild(img);
    }

    const body = document.createElement('div');

    const title = document.createElement('p');
    title.className = 'bh-cart-item__title';
    title.textContent = item.product_title;
    body.appendChild(title);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      const variant = document.createElement('p');
      variant.className = 'bh-cart-item__variant';
      variant.textContent = item.variant_title;
      body.appendChild(variant);
    }

    const row = document.createElement('div');
    row.className = 'bh-cart-item__row';

    const stepper = document.createElement('span');
    stepper.className = 'bh-cart-item__stepper';
    const btn = (delta, label, text) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bh-cart-item__step';
      b.dataset.bhQtyChange = delta;
      b.setAttribute('aria-label', label || '');
      b.textContent = text;
      return b;
    };
    stepper.appendChild(btn('-1', i18n.decrease, '−'));
    const qty = document.createElement('span');
    qty.className = 'bh-cart-item__qty';
    qty.textContent = item.quantity;
    stepper.appendChild(qty);
    stepper.appendChild(btn('1', i18n.increase, '+'));

    const price = document.createElement('span');
    price.className = 'bh-cart-item__price';
    price.textContent = money(item.final_line_price);

    row.appendChild(stepper);
    row.appendChild(price);
    body.appendChild(row);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'bh-cart-item__remove';
    remove.dataset.bhQtyChange = '0';
    remove.textContent = i18n.remove || 'Remove';
    body.appendChild(remove);

    li.appendChild(media);
    li.appendChild(body);
    return li;
  };

  const render = (cart) => {
    if (!drawer) return;
    const list = drawer.querySelector('[data-bh-cart-items]');
    const empty = drawer.querySelector('[data-bh-cart-empty]');
    const foot = drawer.querySelector('[data-bh-cart-foot]');
    const subtotal = drawer.querySelector('[data-bh-cart-subtotal]');

    list.textContent = '';
    cart.items.forEach((item) => list.appendChild(lineItem(item)));

    const has = cart.item_count > 0;
    empty.hidden = has;
    foot.hidden = !has;
    subtotal.textContent = money(cart.items_subtotal_price);
    updateBadges(cart.item_count);
  };

  const openDrawer = (trigger) => {
    if (!drawer || drawer.open) return;
    lastTrigger = trigger || null;
    clearError();
    drawer.showModal();
    // When busy, an add/change chain is already in charge of the fetch,
    // render, and spinner — starting a second chain here would race it.
    if (!fetched && !busy) {
      setLoading(true);
      refreshCart()
        .catch((e) => showError(e.message))
        .finally(() => setLoading(false));
    }
  };

  const closeDrawer = () => {
    if (!drawer || !drawer.open) return;
    drawer.close();
  };

  if (drawer) {
    drawer.addEventListener('close', () => {
      if (lastTrigger && document.contains(lastTrigger)) lastTrigger.focus();
      lastTrigger = null;
    });

    drawer.addEventListener('click', (e) => {
      // Backdrop clicks target the dialog element itself
      if (e.target === drawer) {
        closeDrawer();
        return;
      }
      const closeBtn = e.target.closest('[data-bh-cart-close]');
      if (closeBtn) {
        closeDrawer();
        return;
      }
      const step = e.target.closest('[data-bh-qty-change]');
      if (step && !busy) {
        const line = step.closest('[data-line-key]');
        if (!line) return;
        const current = parseInt(line.querySelector('.bh-cart-item__qty')?.textContent || '1', 10);
        const delta = parseInt(step.dataset.bhQtyChange, 10);
        const next = delta === 0 ? 0 : Math.max(0, current + delta);
        setBusy(true);
        clearError();
        changeLine(line.dataset.lineKey, next)
          .then((cart) => {
            cartSeq += 1; // /cart/change.js returns the full cart; invalidate stragglers
            fetched = true;
            render(cart);
          })
          .catch((e2) => showError(e2.message))
          .finally(() => setBusy(false));
      }
    });

    // Warm the cart JSON while the page is idle so the first drawer open (and
    // the header badge) never waits on a fetch.
    const prefetch = () => refreshCart().catch(() => {});
    if ('requestIdleCallback' in window) {
      requestIdleCallback(prefetch, { timeout: 2000 });
    } else {
      setTimeout(prefetch, 300);
    }

    // bfcache restores resurrect old JS state: after Back from the cart or
    // checkout page (or changes in another tab) the badge and drawer would be
    // stale, so refetch.
    window.addEventListener('pageshow', (e) => {
      if (e.persisted) {
        fetched = false;
        refreshCart(true).catch(() => {});
      }
    });
  }

  // --- Global delegation: gallery slides, buy buttons + cart-open triggers ---
  document.addEventListener('click', (e) => {
    const slideBtn = e.target.closest('[data-bh-slide-to]');
    if (slideBtn) {
      const carousel = slideBtn.closest('[data-bh-carousel]');
      if (carousel) {
        const idx = slideBtn.dataset.bhSlideTo;
        carousel.querySelectorAll('[data-bh-slide]').forEach((s) => {
          s.classList.toggle('is-active', s.dataset.bhSlide === idx);
        });
        carousel.querySelectorAll('[data-bh-slide-to]').forEach((b) => {
          b.classList.toggle('is-active', b === slideBtn);
          b.setAttribute('aria-pressed', b === slideBtn ? 'true' : 'false');
        });
      }
      return;
    }

    const buy = e.target.closest('[data-bh-buy]');
    if (buy && drawer) {
      // Keep repeated clicks inside the enhanced flow while a cart operation
      // is running; never fall through to the native cart-page link.
      if (busy) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      setBusy(true);
      setTriggerBusy(buy, true);
      clearError();
      cartSeq += 1; // any in-flight plain refresh now holds a pre-add snapshot
      addItem({
        id: parseInt(buy.dataset.variantId, 10),
        quantity: parseInt(buy.dataset.quantity || '1', 10),
      })
        .then(() => refreshCart(true))
        // Open only after the fresh cart has rendered, so the device is
        // visible on the first animation frame of the drawer.
        .then(() => openDrawer(buy))
        .catch((e2) => {
          openDrawer(buy);
          showError(e2.message);
        })
        .finally(() => {
          setTriggerBusy(buy, false);
          setBusy(false);
        });
      return;
    }

    const opener = e.target.closest('[data-bh-cart-open]');
    if (opener && drawer) {
      e.preventDefault();
      openDrawer(opener);
    }
  });

  // Product form: intercept submit in drawer mode
  document.addEventListener('submit', (e) => {
    const form = e.target.closest('[data-bh-atc-form]');
    if (!form || !drawer) return;
    if (busy) {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    const errRegion = form.querySelector('[data-bh-form-error]');
    const trigger = e.submitter || form.querySelector('[type="submit"]');
    if (errRegion) errRegion.hidden = true;
    setBusy(true);
    setTriggerBusy(trigger, true);
    cartSeq += 1; // any in-flight plain refresh now holds a pre-add snapshot
    addForm(form)
      .then(() => refreshCart(true))
      // The drawer now opens with the submitted item already rendered.
      .then(() => openDrawer(trigger))
      .catch((e2) => {
        openDrawer(trigger);
        showError(e2.message);
        if (errRegion) {
          errRegion.textContent = e2.message;
          errRegion.hidden = false;
        }
      })
      .finally(() => {
        setTriggerBusy(trigger, false);
        setBusy(false);
      });
  });

  // --- Product purchase enhancements (mode-independent) ---
  // Delegated and scoped to the button's own form so the same hooks work in
  // every purchase UI (product page, monitor section, device hero).
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-bh-qty-step]');
    if (!btn) return;
    const scope = btn.closest('form') || document;
    const input = scope.querySelector('input[name="quantity"]');
    if (!input) return;
    input.value = Math.max(1, (parseInt(input.value, 10) || 1) + parseInt(btn.dataset.bhQtyStep, 10));
  });

  document.addEventListener('change', (e) => {
    const select = e.target.closest('select[name="id"]');
    if (!select || !select.form) return;
    const scope = select.form.closest('[data-bh-buy-scope]') || select.form.closest('section') || document;
    const variantsEl = scope.querySelector('[data-bh-variants]') || document.querySelector('[data-bh-variants]');
    if (!variantsEl) return;
    let variants = [];
    try {
      variants = JSON.parse(variantsEl.textContent);
    } catch (err) {
      variants = [];
    }
    const v = variants.find((x) => String(x.id) === select.value);
    if (!v) return;
    const priceEl = scope.querySelector('[data-bh-price]');
    if (priceEl) priceEl.textContent = money(v.price);
    const barPriceEls = scope.querySelectorAll('[data-bh-bar-price]');
    barPriceEls.forEach((barPriceEl) => {
      barPriceEl.textContent = money(v.price);
    });
    const compareEls = scope.querySelectorAll('[data-bh-compare-price], [data-bh-bar-compare-price]');
    const onSale = v.compare_at_price && v.compare_at_price > v.price;
    compareEls.forEach((compareEl) => {
      compareEl.hidden = !onSale;
      if (onSale) compareEl.textContent = money(v.compare_at_price);
    });
    const discountEls = scope.querySelectorAll('[data-bh-discount], [data-bh-bar-discount]');
    discountEls.forEach((discountEl) => {
      discountEl.hidden = !onSale;
      if (onSale) {
        const percentage = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
        discountEl.textContent = percentage + '% ' + (discountEl.dataset.label || 'off');
      }
    });
    const atc = select.form.querySelector('[type="submit"][data-label-add]');
    if (atc) {
      atc.disabled = !v.available;
      atc.textContent = v.available ? atc.dataset.labelAdd : atc.dataset.labelSoldOut;
    }
  });

  // --- Sticky buy bar (device hero) ---
  // Lives here rather than bh-animations.js: the bar must work under
  // prefers-reduced-motion, where the reveal system bails out entirely.
  const stickyBar = document.querySelector('[data-bh-sticky-bar]');
  const stickySection = stickyBar && document.getElementById('buy');
  const stickyPurchase = stickySection?.querySelector('.bh-device-hero__buy-row');
  const stickyMobile = window.matchMedia('(max-width: 749px)');
  let stickyObserver = null;

  const setStickyVisible = (show) => {
    if (!stickyBar) return;
    stickyBar.classList.toggle('is-visible', show);
    document.body.classList.toggle('bh-stickybar-visible', show);
  };

  const watchStickyCart = () => {
    if (!stickyBar || !stickySection || !('IntersectionObserver' in window)) return;
    if (stickyObserver) stickyObserver.disconnect();

    // Reveal the bar after the primary purchase controls scroll above the
    // viewport on both desktop and mobile, then hide it again when they return.
    const watched = stickyPurchase || stickySection;
    stickyBar.hidden = false;
    setStickyVisible(false);

    stickyObserver = new IntersectionObserver((entries) => {
      const entry = entries[entries.length - 1];
      const hasPassedAbove = entry.boundingClientRect.bottom <= 0;
      const show = !entry.isIntersecting && hasPassedAbove;
      setStickyVisible(show);
    });
    stickyObserver.observe(watched);
  };

  if (stickyBar && stickySection) {
    watchStickyCart();
    if (stickyMobile.addEventListener) stickyMobile.addEventListener('change', watchStickyCart);
    else if (stickyMobile.addListener) stickyMobile.addListener(watchStickyCart);
  }

  document.querySelectorAll('[data-bh-gallery-thumb]').forEach((thumb) => {
    thumb.addEventListener('click', () => {
      const main = document.querySelector('[data-bh-gallery-main]');
      if (main && thumb.dataset.src) {
        // The main image ships with a srcset which would override src
        main.removeAttribute('srcset');
        main.removeAttribute('sizes');
        main.src = thumb.dataset.src;
        document
          .querySelectorAll('[data-bh-gallery-thumb]')
          .forEach((t) => t.classList.toggle('is-active', t === thumb));
      }
    });
  });
})();
